import type { TelemetryRow } from 'aevocam-rest-api-spec'

export const TELEMETRY_TIME_ZONE = 'America/New_York'
export const TELEMETRY_PERIOD_SEC = 3600
export const TELEMETRY_BUCKET_COUNT = 48
export const TELEMETRY_WINDOW_MS = TELEMETRY_BUCKET_COUNT * TELEMETRY_PERIOD_SEC * 1000
export const TELEMETRY_DAILY_PERIOD_SEC = 86400
export const TELEMETRY_DAILY_BUCKET_COUNT = 60
export const TELEMETRY_REFRESH_MS = 10_000
const HOUR_MS = TELEMETRY_PERIOD_SEC * 1000
const DAY_MS = TELEMETRY_DAILY_PERIOD_SEC * 1000

const SERVICE_LABELS: Record<string, string> = {
  LND: 'Landing',
  APP: 'App',
  API: 'API',
  FTP: 'FTP ingest',
  ING: 'Ingest processor',
  OBS: 'Observatory',
  BIL: 'Billing',
}

const SERIES_LABELS: Record<string, string> = {
  'FTP|A': 'FTP auth attempts',
  'FTP|P': 'Photos uploaded',
  'ING|P': 'Photos ingested',
}

export type TelemetrySeries = {
  key: string
  service: string
  metric: string
  label: string
  bucketFrom: Date
  buckets: number[]
  hourlyAverage: number
  dailyBucketFrom: Date
  dailyBuckets: number[]
  dailyAverage: number
  total: number
}

export function formatTelemetryBucketTime(start: Date, periodSec = TELEMETRY_PERIOD_SEC): string {
  if (periodSec === TELEMETRY_DAILY_PERIOD_SEC) {
    return start.toLocaleString('en-US', {
      timeZone: TELEMETRY_TIME_ZONE,
      month: 'short',
      day: 'numeric',
    }) + ' ET'
  }
  return start.toLocaleString('en-US', {
    timeZone: TELEMETRY_TIME_ZONE,
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    hour12: true,
  }) + ' ET'
}

/** Label for a telemetry bucket period (eg 3600 → "hour") */
export function formatTelemetryPeriodUnit(periodSec: number): string {
  if (periodSec === 3600) return 'hour'
  if (periodSec === 86400) return 'day'
  if (periodSec === 5) return '5 sec'
  if (periodSec % 86400 === 0) return `${periodSec / 86400} days`
  if (periodSec % 3600 === 0) return `${periodSec / 3600} hours`
  if (periodSec % 60 === 0) return `${periodSec / 60} min`
  return `${periodSec} sec`
}

export function telemetrySeriesGroupKey(row: TelemetryRow): string {
  return [
    row.service ?? '',
    row.metric ?? '',
    row.aggregation ?? '',
  ].join(':')
}

export function telemetrySeriesKey(row: TelemetryRow): string {
  return [
    row.service ?? '',
    row.metric ?? '',
    row.aggregation ?? '',
    row.period ?? '',
  ].join(':')
}

export function formatTelemetryLabel(service: string, metric: string): string {
  const known = SERIES_LABELS[`${service}|${metric}`]
  if (known) return known
  const serviceLabel = SERVICE_LABELS[service] ?? service
  return `${serviceLabel} · ${metric}`
}

/** UTC instant for the start of the day containing `date` in `timeZone`. */
export function startOfDayInTimeZone(date: Date, timeZone: string): Date {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date)

  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find(p => p.type === type)?.value ?? '0'

  const year = +part('year')
  const month = +part('month')
  const day = +part('day')

  const utcGuess = Date.UTC(year, month - 1, day, 0, 0, 0)
  const offsetMs = getTimeZoneOffsetMs(timeZone, new Date(utcGuess))
  return new Date(utcGuess - offsetMs)
}

/** UTC instant for the start of the hour containing `date` in `timeZone`. */
export function startOfHourInTimeZone(date: Date, timeZone: string): Date {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date)

  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find(p => p.type === type)?.value ?? '0'

  const year = +part('year')
  const month = +part('month')
  const day = +part('day')
  const hour = +part('hour')

  const utcGuess = Date.UTC(year, month - 1, day, hour, 0, 0)
  const offsetMs = getTimeZoneOffsetMs(timeZone, new Date(utcGuess))
  return new Date(utcGuess - offsetMs)
}

function getTimeZoneOffsetMs(timeZone: string, date: Date): number {
  const utc = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }))
  const zoned = new Date(date.toLocaleString('en-US', { timeZone }))
  return zoned.getTime() - utc.getTime()
}

export function telemetryWindow(now = new Date()) {
  const currentHourStart = startOfHourInTimeZone(now, TELEMETRY_TIME_ZONE)
  const from = new Date(
    currentHourStart.getTime() - (TELEMETRY_BUCKET_COUNT - 1) * HOUR_MS,
  )
  const to = new Date(currentHourStart.getTime() + HOUR_MS)
  return { from, to, currentHourStart, now }
}

export function telemetryDailyWindow(now = new Date()) {
  const currentDayStart = startOfDayInTimeZone(now, TELEMETRY_TIME_ZONE)
  const from = new Date(
    currentDayStart.getTime() - (TELEMETRY_DAILY_BUCKET_COUNT - 1) * DAY_MS,
  )
  const to = new Date(currentDayStart.getTime() + DAY_MS)
  return { from, to, currentDayStart, now }
}

export function buildTelemetryTimeSeries(
  rows: TelemetryRow[],
  from: Date,
  to: Date,
  periodSec: number,
): number[] {
  const periodMs = periodSec * 1000
  const slotCount = Math.ceil((to.getTime() - from.getTime()) / periodMs)
  const buckets = new Array<number>(slotCount).fill(0)
  const fromMs = from.getTime()

  for (const row of rows) {
    if (row.startedAt == null || row.value == null) continue
    const idx = Math.floor((new Date(row.startedAt).getTime() - fromMs) / periodMs)
    if (idx >= 0 && idx < slotCount) {
      buckets[idx] = (buckets[idx] ?? 0) + row.value
    }
  }

  return buckets
}

/**
 * Estimate the current hour's total by blending the in-progress hour with a
 * pro-rated share of the prior complete hour.
 */
export function estimatedHourlyAverage(
  buckets: number[],
  now: Date,
): number {
  if (buckets.length === 0) return 0

  const currentPartial = buckets[buckets.length - 1] ?? 0
  const priorTotal = buckets.length >= 2 ? buckets[buckets.length - 2]! : 0

  const hourStart = startOfHourInTimeZone(now, TELEMETRY_TIME_ZONE)
  const elapsedMs = now.getTime() - hourStart.getTime()
  const elapsedFraction = Math.min(1, Math.max(0, elapsedMs / HOUR_MS))
  const priorFraction = 1 - elapsedFraction

  if (buckets.length === 1 && elapsedFraction > 0) {
    return currentPartial / elapsedFraction
  }

  return currentPartial + priorTotal * priorFraction
}

/**
 * Estimate the current day's total by blending the in-progress day with a
 * pro-rated share of the prior complete day.
 */
export function estimatedDailyAverage(
  buckets: number[],
  now: Date,
): number {
  if (buckets.length === 0) return 0

  const currentPartial = buckets[buckets.length - 1] ?? 0
  const priorTotal = buckets.length >= 2 ? buckets[buckets.length - 2]! : 0

  const dayStart = startOfDayInTimeZone(now, TELEMETRY_TIME_ZONE)
  const elapsedMs = now.getTime() - dayStart.getTime()
  const elapsedFraction = Math.min(1, Math.max(0, elapsedMs / DAY_MS))
  const priorFraction = 1 - elapsedFraction

  if (buckets.length === 1 && elapsedFraction > 0) {
    return currentPartial / elapsedFraction
  }

  return currentPartial + priorTotal * priorFraction
}

export function rowsToTelemetrySeries(
  rows: TelemetryRow[],
  hourlyFrom: Date,
  hourlyTo: Date,
  dailyFrom: Date,
  dailyTo: Date,
  now: Date,
): TelemetrySeries[] {
  const grouped = new Map<string, TelemetryRow[]>()

  for (const row of rows) {
    if (row.period !== TELEMETRY_PERIOD_SEC && row.period !== TELEMETRY_DAILY_PERIOD_SEC) {
      continue
    }
    const key = telemetrySeriesGroupKey(row)
    const group = grouped.get(key) ?? []
    group.push(row)
    grouped.set(key, group)
  }

  const series = [...grouped.entries()].map(([key, groupRows]) => {
    const sample = groupRows[0]!
    const service = sample.service ?? ''
    const metric = sample.metric ?? ''
    const hourlyRows = groupRows.filter(row => row.period === TELEMETRY_PERIOD_SEC)
    const dailyRows = groupRows.filter(row => row.period === TELEMETRY_DAILY_PERIOD_SEC)
    const buckets = buildTelemetryTimeSeries(hourlyRows, hourlyFrom, hourlyTo, TELEMETRY_PERIOD_SEC)
    const dailyBuckets = buildTelemetryTimeSeries(
      dailyRows,
      dailyFrom,
      dailyTo,
      TELEMETRY_DAILY_PERIOD_SEC,
    )
    const total = buckets.reduce((sum, value) => sum + value, 0)

    return {
      key,
      service,
      metric,
      label: formatTelemetryLabel(service, metric),
      bucketFrom: hourlyFrom,
      buckets,
      hourlyAverage: estimatedHourlyAverage(buckets, now),
      dailyBucketFrom: dailyFrom,
      dailyBuckets,
      dailyAverage: estimatedDailyAverage(dailyBuckets, now),
      total,
    }
  })

  return series.sort((a, b) =>
    a.service.localeCompare(b.service) ||
    a.metric.localeCompare(b.metric),
  )
}
